// Welcome Bonus — client status + claim.
// Backend: GET /payment/signup-bonus, POST /payment/signup-bonus/claim

import { apiService } from '@/services/apiService';

export type SignupBonusStatus = 'inactive' | 'active' | 'in_review' | 'completed' | 'expired';

export interface SignupBonus {
    status: SignupBonusStatus;
    grantedAmount: number;
    realAmount: number;
    /** Live event P&L since claim (not all-time). */
    netWin: number;
    /** Peak event P&L for progress bars (matches admin/shop). */
    peakNetWin: number;
    winThreshold: number;
    netWinMet: boolean;
    /** Live loss unlock: floor lifted while event P&L ≤ −grant. */
    floorLiftedByLoss?: boolean;
    /** @deprecated Deposit is no longer a quest — kept for API compat. */
    depositedTotal?: number;
    minDeposit?: number;
    depositMet?: boolean;
    expiresAt: string;
    grantedAt?: string | null;
    /** When the bonus expired (status `expired`). Omitted by APIs that do not report it yet. */
    expiredAt?: string | null;
    /**
     * Unclaimed bonus only: false when the shop / platform has the bonus switched off for this
     * player (do not advertise it). Omitted by older APIs.
     */
    offered?: boolean;
    /** Present when inactive and claim is blocked (e.g. daily limit, unverified contact, shop opted out). */
    claimBlockCode?: string | null;
    claimBlockMessage?: string | null;
    /** Admin setting: claim needs verified phone, email, or both. */
    claimVerifyMode?: 'phone' | 'email' | 'both' | null;
}

/** Friendly copy when daily claim budget is exhausted. */
export const DAILY_BUDGET_USER_MESSAGE =
  "Today's welcome bonus limit has been reached. Please wait until tomorrow to claim.";


export async function fetchSignupBonus(forceRefresh = false): Promise<SignupBonus | null> {
    const res = await apiService.get<{ bonus: SignupBonus | null }>(
        '/payment/signup-bonus',
        true,
        forceRefresh
    );
    return res?.data?.bonus ?? null;
}

export async function claimSignupBonus(): Promise<SignupBonus | null> {
    const res = await apiService.post<{ bonus: SignupBonus | null }>(
        '/payment/signup-bonus/claim',
        {}
    );
    return res?.data?.bonus ?? null;
}
